/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/toast';
import { useDeliveryZip, useZipFlash } from '@/providers/store-and-delivery-zip-provider';
import { ZIP_FLASH } from '@/lib/store-resolution-constants';

/**
 * BROWSE-47 — One-shot toast that fires when the store-resolution middleware
 * overwrote a shopper's MANUALLY-entered delivery ZIP because the newly
 * resolved store is in a different price zone (flow diagram Step C, manual
 * branch → ZONE_UPDATED). Same-zone resolutions and auto-derived overwrites are
 * silent, so this component never fires for those.
 *
 * The middleware sets the `x-zip-flash` header on the SSR response and the
 * provider surfaces it as a single render's `flash` value. This component
 * watches that value and dispatches exactly one toast per request — guarded
 * by a ref so React 18 strict-mode double-effect doesn't double-toast.
 *
 * Renders nothing — this is a side-effect-only component. Mount once in the
 * root layout next to the existing ToasterTheme.
 */
export function ZipUpdateToast() {
    const { t } = useTranslation('delivery');
    const { addToast } = useToast();
    const flash = useZipFlash();
    const { zip } = useDeliveryZip();
    const firedRef = useRef<string | null>(null);

    useEffect(() => {
        if (flash !== ZIP_FLASH.ZONE_UPDATED) return;
        // Guard against StrictMode double-invocation and against the same flash
        // re-firing on re-render (provider memoizes by reference, but a
        // navigation to a route that re-uses the same root loader payload
        // would re-emit). Keying by zip is sufficient: the middleware only
        // sets the flash on the request that produced *this* zip.
        if (firedRef.current === zip) return;
        firedRef.current = zip;

        addToast(t('zipUpdated.title'), 'info', {
            description: t('zipUpdated.description', { zip }),
        });
    }, [flash, zip, addToast, t]);

    return null;
}

export default ZipUpdateToast;
